// src/finance/api.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import type { FinanceSettings, FinanceMonth, RecurringExpense } from '../lib/types'
import { DEFAULT_FINANCE_SETTINGS } from './engine/defaults'

export const financeKeys = {
  all: ['finance'] as const,
  settings: ['finance', 'settings'] as const,
  expenses: ['finance', 'expenses'] as const,
  months: (year: number) => ['finance', 'months', year] as const,
}

// Invalide les mois de *toutes* les années (préfixe sans le paramètre `year`).
// Déclaré ici parce que la suppression d'une récurrente s'en sert aussi.
function useInvalidateMonths() {
  const queryClient = useQueryClient()
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['finance', 'months'] })
  }
}

// --- Settings (singleton, null serveur → défauts moteur) ---

export function useFinanceSettings() {
  return useQuery<FinanceSettings, Error>({
    queryKey: financeKeys.settings,
    queryFn: () =>
      api
        .get<{ settings: FinanceSettings | null }>('/api/finance/settings')
        // Fusion défauts ⊕ stockés : un settings enregistré AVANT l'ajout de
        // nouveaux champs fiscaux (ex. IS français) doit être complété — le
        // moteur ne doit jamais lire `undefined` (→ NaN silencieux).
        .then((r) =>
          r.settings === null
            ? DEFAULT_FINANCE_SETTINGS
            : {
                ...DEFAULT_FINANCE_SETTINGS,
                ...r.settings,
                tax: { ...DEFAULT_FINANCE_SETTINGS.tax, ...r.settings.tax },
              },
        ),
    staleTime: 1000 * 60 * 5,
  })
}

export function useSaveFinanceSettings() {
  const queryClient = useQueryClient()
  return useMutation<FinanceSettings, Error, FinanceSettings>({
    mutationFn: (body) =>
      api.put<{ settings: FinanceSettings }>('/api/finance/settings', body).then((r) => r.settings),
    onSuccess: (settings) => {
      queryClient.setQueryData(financeKeys.settings, settings)
    },
  })
}

// --- Dépenses récurrentes ---

export type CreateRecurringExpenseInput = {
  kind: 'company' | 'personal'
  label: string
  amount: number
  active?: boolean | undefined
  sortOrder?: number | undefined
  periodicity?: 'monthly' | 'yearly' | undefined
  dueMonth?: number | null | undefined
}

export type UpdateRecurringExpenseInput = { id: string } & Partial<CreateRecurringExpenseInput>

export function useRecurringExpenses() {
  return useQuery<RecurringExpense[], Error>({
    queryKey: financeKeys.expenses,
    queryFn: () =>
      api.get<{ expenses: RecurringExpense[] }>('/api/finance/expenses').then((r) => r.expenses),
    staleTime: 1000 * 60 * 5,
  })
}

export function useCreateRecurringExpense() {
  const queryClient = useQueryClient()
  return useMutation<RecurringExpense, Error, CreateRecurringExpenseInput>({
    mutationFn: (body) =>
      api.post<{ expense: RecurringExpense }>('/api/finance/expenses', body).then((r) => r.expense),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.expenses })
    },
  })
}

export function useUpdateRecurringExpense() {
  const queryClient = useQueryClient()
  return useMutation<RecurringExpense, Error, UpdateRecurringExpenseInput>({
    mutationFn: ({ id, ...body }) =>
      api
        .put<{ expense: RecurringExpense }>(`/api/finance/expenses/${id}`, body)
        .then((r) => r.expense),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.expenses })
    },
  })
}

export function useDeleteRecurringExpense() {
  const queryClient = useQueryClient()
  const invalidateMonths = useInvalidateMonths()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.del<{ ok: boolean }>(`/api/finance/expenses/${id}`).then(() => undefined),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: financeKeys.expenses })
      // `finance_month_expenses.recurring_id` est ON DELETE SET NULL (migration 0010) :
      // la suppression modifie aussi les lignes de mois côté serveur.
      invalidateMonths()
    },
  })
}

// --- Mois réels ---

export type MonthExpenseInput = { label: string; amount: number; recurringId: string | null }

export type CreateFinanceMonthInput = {
  month: string
  revenue?: number | undefined
  salaryPaid?: number | undefined
  dividendsPaid?: number | undefined
  note?: string | null | undefined
  expenses?: MonthExpenseInput[] | undefined
  // Objectifs FIGÉS à la création (migration 0012), calculés CÔTÉ CLIENT : le moteur
  // de simulation vit dans src/finance/engine et la seule frontière functions/ → src/
  // autorisée est le type-only src/lib/types.ts. Le serveur les valide (Zod) et les
  // stocke tels quels ; le PUT ne les accepte pas (immuables).
  targetRevenue?: number | null | undefined
  targetNet?: number | null | undefined
  targetExpenses?: number | null | undefined
}

export type UpdateFinanceMonthInput = { id: string } & Omit<
  Partial<CreateFinanceMonthInput>,
  'month' | 'targetRevenue' | 'targetNet' | 'targetExpenses'
>

export function useFinanceMonths(year: number) {
  return useQuery<FinanceMonth[], Error>({
    queryKey: financeKeys.months(year),
    queryFn: () =>
      api.get<{ months: FinanceMonth[] }>(`/api/finance/months?year=${year}`).then((r) => r.months),
    staleTime: 1000 * 60 * 2,
  })
}

export function useCreateFinanceMonth() {
  const invalidate = useInvalidateMonths()
  return useMutation<FinanceMonth, Error, CreateFinanceMonthInput>({
    mutationFn: (body) =>
      api.post<{ month: FinanceMonth }>('/api/finance/months', body).then((r) => r.month),
    onSuccess: invalidate,
  })
}

export function useUpdateFinanceMonth() {
  const invalidate = useInvalidateMonths()
  return useMutation<FinanceMonth, Error, UpdateFinanceMonthInput>({
    mutationFn: ({ id, ...body }) =>
      api.put<{ month: FinanceMonth }>(`/api/finance/months/${id}`, body).then((r) => r.month),
    onSuccess: invalidate,
  })
}

export function useDeleteFinanceMonth() {
  const invalidate = useInvalidateMonths()
  return useMutation<void, Error, string>({
    mutationFn: (id) => api.del<{ ok: boolean }>(`/api/finance/months/${id}`).then(() => undefined),
    onSuccess: invalidate,
  })
}
